import type {
  AppPreferences,
  CheckboxOption,
  IssueField,
  IssueForm,
  PersistedState,
  RepositorySettings,
} from '../domain/types';
import { createUid } from '../domain/utils';

export const PERSISTED_STATE_KEY = 'issue-mint.state.v1';

export interface StorageLike {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
  removeItem(key: string): void;
}

type PersistedStateInput = Omit<PersistedState, 'version'> | PersistedState;
type UnknownRecord = Record<string, unknown>;

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function stringValue(value: unknown): string | null {
  return typeof value === 'string' ? value : null;
}

function booleanValue(value: unknown): boolean | null {
  return typeof value === 'boolean' ? value : null;
}

function uidValue(value: unknown, prefix: string): string {
  return typeof value === 'string' && value !== '' ? value : createUid(prefix);
}

function stringList(value: unknown): string[] | null {
  if (!Array.isArray(value) || !value.every((entry) => typeof entry === 'string')) return null;
  return [...value];
}

function restoreCheckboxOption(value: unknown): CheckboxOption | null {
  if (!isRecord(value)) return null;
  const label = stringValue(value.label);
  const required = booleanValue(value.required);
  if (label === null || required === null) return null;
  return { uid: uidValue(value.uid, 'option'), label, required };
}

function restoreField(value: unknown): IssueField | null {
  if (!isRecord(value) || typeof value.type !== 'string' || !isRecord(value.attributes)) {
    return null;
  }
  const uid = uidValue(value.uid, 'field');
  const attributes = value.attributes;

  if (value.type === 'markdown') {
    const markdownValue = stringValue(attributes.value);
    return markdownValue === null
      ? null
      : { uid, type: 'markdown', attributes: { value: markdownValue } };
  }

  const id = stringValue(value.id);
  const label = stringValue(attributes.label);
  const description = stringValue(attributes.description);
  if (id === null || label === null || description === null) return null;

  if (value.type === 'checkboxes') {
    if (!Array.isArray(attributes.options)) return null;
    const options = attributes.options.map(restoreCheckboxOption);
    if (options.some((option) => option === null)) return null;
    return {
      uid,
      type: 'checkboxes',
      id,
      attributes: {
        label,
        description,
        options: options as CheckboxOption[],
      },
    };
  }

  if (!isRecord(value.validations)) return null;
  const required = booleanValue(value.validations.required);
  if (required === null) return null;

  if (value.type === 'input') {
    const placeholder = stringValue(attributes.placeholder);
    const inputValue = stringValue(attributes.value);
    if (placeholder === null || inputValue === null) return null;
    return {
      uid,
      type: 'input',
      id,
      attributes: { label, description, placeholder, value: inputValue },
      validations: { required },
    };
  }

  if (value.type === 'textarea') {
    const placeholder = stringValue(attributes.placeholder);
    const textareaValue = stringValue(attributes.value);
    const render = stringValue(attributes.render);
    if (placeholder === null || textareaValue === null || render === null) return null;
    return {
      uid,
      type: 'textarea',
      id,
      attributes: { label, description, placeholder, value: textareaValue, render },
      validations: { required },
    };
  }

  if (value.type === 'dropdown') {
    const options = stringList(attributes.options);
    const multiple = booleanValue(attributes.multiple);
    if (options === null || multiple === null) return null;
    return {
      uid,
      type: 'dropdown',
      id,
      attributes: { label, description, options, multiple },
      validations: { required },
    };
  }

  return null;
}

function restoreForm(value: unknown): IssueForm | null {
  if (!isRecord(value)) return null;
  const filename = stringValue(value.filename);
  const name = stringValue(value.name);
  const description = stringValue(value.description);
  const title = stringValue(value.title);
  const labels = stringList(value.labels);
  const assignees = stringList(value.assignees);
  if (
    filename === null ||
    name === null ||
    description === null ||
    title === null ||
    labels === null ||
    assignees === null ||
    !Array.isArray(value.body)
  ) {
    return null;
  }
  const body = value.body.map(restoreField);
  if (body.some((field) => field === null)) return null;
  return {
    uid: uidValue(value.uid, 'form'),
    filename,
    name,
    description,
    title,
    labels,
    assignees,
    body: body as IssueField[],
  };
}

function restoreRepositorySettings(value: unknown): RepositorySettings | null {
  if (!isRecord(value)) return null;
  const blankIssuesEnabled = booleanValue(value.blankIssuesEnabled);
  if (blankIssuesEnabled === null || !Array.isArray(value.contactLinks)) return null;
  const contactLinks = value.contactLinks.map((entry) => {
    if (!isRecord(entry)) return null;
    const name = stringValue(entry.name);
    const url = stringValue(entry.url);
    const about = stringValue(entry.about);
    if (name === null || url === null || about === null) return null;
    return { uid: uidValue(entry.uid, 'contact'), name, url, about };
  });
  if (contactLinks.some((link) => link === null)) return null;
  return {
    blankIssuesEnabled,
    contactLinks: contactLinks as RepositorySettings['contactLinks'],
  };
}

function resolveStorage(storage?: StorageLike): StorageLike | null {
  if (storage) return storage;
  try {
    return globalThis.localStorage;
  } catch {
    return null;
  }
}

function isStorageLike(value: unknown): value is StorageLike {
  return (
    isRecord(value) &&
    typeof value.getItem === 'function' &&
    typeof value.setItem === 'function' &&
    typeof value.removeItem === 'function'
  );
}

export function loadPersistedState(storage?: StorageLike): PersistedState | null {
  const target = resolveStorage(storage);
  if (!target) return null;
  try {
    const serialized = target.getItem(PERSISTED_STATE_KEY);
    if (serialized === null) return null;
    const value: unknown = JSON.parse(serialized);
    if (!isRecord(value) || value.version !== 1) return null;
    const form = restoreForm(value.form);
    const repositorySettings = restoreRepositorySettings(value.repositorySettings);
    if (
      form === null ||
      repositorySettings === null ||
      (value.language !== 'en' && value.language !== 'zh') ||
      (value.theme !== 'light' && value.theme !== 'dark')
    ) {
      return null;
    }
    return {
      version: 1,
      form,
      language: value.language,
      theme: value.theme,
      repositorySettings,
    };
  } catch {
    return null;
  }
}

export function savePersistedState(state: PersistedStateInput, storage?: StorageLike): boolean;
export function savePersistedState(
  form: IssueForm,
  preferences: AppPreferences,
  storage?: StorageLike,
): boolean;
export function savePersistedState(
  stateOrForm: PersistedStateInput | IssueForm,
  preferencesOrStorage?: AppPreferences | StorageLike,
  maybeStorage?: StorageLike,
): boolean {
  const passedPreferences =
    preferencesOrStorage !== undefined && !isStorageLike(preferencesOrStorage)
      ? preferencesOrStorage
      : undefined;
  const storage = isStorageLike(preferencesOrStorage) ? preferencesOrStorage : maybeStorage;
  const state: PersistedState = passedPreferences
    ? { version: 1, form: stateOrForm as IssueForm, ...passedPreferences }
    : { version: 1, ...(stateOrForm as PersistedStateInput) };
  const target = resolveStorage(storage);
  if (!target) return false;
  try {
    target.setItem(PERSISTED_STATE_KEY, JSON.stringify(state));
    return true;
  } catch {
    return false;
  }
}

export function clearPersistedState(storage?: StorageLike): boolean {
  const target = resolveStorage(storage);
  if (!target) return false;
  try {
    target.removeItem(PERSISTED_STATE_KEY);
    return true;
  } catch {
    return false;
  }
}

export const restoreAppState = loadPersistedState;
export const saveAppState = savePersistedState;
export const clearAppState = clearPersistedState;
